import { useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { DateInput } from "./ui/date-input";

export const ACT_KIND_LABELS = {
  assignment: "Πράξη Ορισμού Δικαστή/Σύνθεσης",
  rapporteur: "Πράξη Ανάθεσης Εισηγητή",
  reassignment: "Πράξη ΑΝΑΧΡΕΩΣΗΣ (Νέου Ορισμού)",
};

// Κουμπί «μολύβι» + διάλογος τροποποίησης Αριθμού/Έτους Πράξης — Admin & Γραμματεία (όχι ρόλος judge)
// onSaved({act_number, act_year, kind}) καλείται μετά την επιτυχή αποθήκευση
export const ActNumberButton = ({ caseId, kind, number, year, issueDate = "", gak = "", onSaved, className = "" }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [num, setNum] = useState("");
  const [yr, setYr] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null); // {message, cases[]}
  if (!number || user?.role === "judge") return null;
  const label = ACT_KIND_LABELS[kind] || "Πράξη";

  const start = (e) => {
    e?.stopPropagation?.();
    setNum(String(number)); setYr(String(year || "")); setDate((issueDate || "").slice(0, 10)); setDup(null); setOpen(true);
  };

  const save = async (force = false) => {
    if (!num.trim()) { toast.error("Δώστε αριθμό πράξης"); return; }
    try {
      setBusy(true);
      const r = await axios.put(`${API}/cases/${caseId}/act-number`, { kind, act_number: num.trim(), act_year: yr.trim() || undefined, issue_date: date || undefined, force });
      toast.success(r.data?.message || "Ο αριθμός πράξης ενημερώθηκε", { duration: 8000 });
      setOpen(false); setDup(null);
      onSaved?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "duplicate_act_number") { setDup(d); return; }
      toast.error(typeof d === "string" ? d : (d?.message || "Σφάλμα τροποποίησης αριθμού πράξης"), { duration: 10000 });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="ghost" size="icon" className={`h-6 w-6 text-slate-500 hover:text-blue-700 hover:bg-blue-50 ${className}`}
              title={`Τροποποίηση αριθμού — ${label}`} onClick={start} data-testid={`edit-act-number-${kind}-${caseId}`}>
        <Pencil className="w-3.5 h-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); setDup(null); } }}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()} data-testid="act-number-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Τροποποίηση Αριθμού / Ημερομηνίας Πράξης</DialogTitle>
            <DialogDescription>
              {label}{gak ? ` — ΓΑΚ ${gak}` : ""}. Τρέχων: <b className="font-mono">{number}/{year}</b>.
              Η αποθηκευμένη πράξη ενημερώνεται — οι επόμενες λήψεις DOCX/PDF θα φέρουν τον νέο αριθμό/ημερομηνία.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[1fr_90px_150px] gap-3">
            <div>
              <Label className="text-xs text-slate-500">Αριθμός Πράξης</Label>
              <Input value={num} onChange={(e) => { setNum(e.target.value); setDup(null); }} className="font-mono" autoFocus
                     data-testid="act-number-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Έτος</Label>
              <Input value={yr} onChange={(e) => { setYr(e.target.value); setDup(null); }} className="font-mono" maxLength={4}
                     data-testid="act-year-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Ημ. Έκδοσης</Label>
              <DateInput value={date} onChange={(e) => setDate(e.target.value)} data-testid="act-issue-date-input" />
            </div>
          </div>
          {dup && (
            <div className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1" data-testid="act-number-duplicate-warning">
              <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Προειδοποίηση — ο αριθμός υπάρχει ήδη</p>
              <ul className="list-disc ml-4">
                {(dup.cases || []).map(c => <li key={c.id}>ΓΑΚ <b>{c.gak}</b>{c.acts?.length ? ` — ${c.acts.join(", ")}` : ""}</li>)}
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setOpen(false); setDup(null); }} disabled={busy} data-testid="act-number-cancel">Άκυρο</Button>
            {dup ? (
              <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => save(true)} disabled={busy} data-testid="act-number-force-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Αποθήκευση παρ' όλα αυτά"}
              </Button>
            ) : (
              <Button onClick={() => save(false)} disabled={busy} data-testid="act-number-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Αποθήκευση"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActNumberButton;

// Κουμπί «κάδος» — ΔΙΑΓΡΑΦΗ αριθμού Πράξης (η υπόθεση επανέρχεται σε «δεν έχει εκδοθεί»). Admin & Γραμματεία.
export const ActNumberDeleteButton = ({ caseId, kind, number, year, gak = "", onDeleted, className = "" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!number || user?.role === "judge") return null;
  const label = ACT_KIND_LABELS[kind] || "Πράξη";
  const del = async (e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Διαγραφή ${label} ${number}/${year || ""}${gak ? ` — ΓΑΚ ${gak}` : ""}; Η υπόθεση θα εμφανίζεται ως «δεν έχει εκδοθεί».`)) return;
    try {
      setBusy(true);
      const r = await axios.delete(`${API}/cases/${caseId}/act-number/${kind}`);
      toast.success(r.data?.message || "Ο αριθμός πράξης διαγράφηκε", { duration: 8000 });
      onDeleted?.(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Σφάλμα διαγραφής αριθμού πράξης", { duration: 10000 });
    } finally { setBusy(false); }
  };
  return (
    <Button variant="ghost" size="icon" className={`h-6 w-6 text-slate-500 hover:text-red-700 hover:bg-red-50 ${className}`}
            title={`Διαγραφή αριθμού — ${label}`} onClick={del} disabled={busy} data-testid={`delete-act-number-${kind}-${caseId}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
    </Button>
  );
};
